import React from 'react'
import { Table, Tabs, Tab } from 'react-bootstrap'
import map from 'lodash/map'
import { t } from '../../../i18n'
import CommandTableView from './CommandTableView'
import CommandGraphView from './CommandGraphView'
import DosingAmountHistory, { DosingFilterOptions } from './DosingAmountHistory'
import { LocalizationContext, LocalizationContextType, DEFAULT_LOCALIZATION_CONTEXT } from '../../shared/contexts'
import { getParam, applyParams, FILTER_PARAM_KEYS } from './urlParams'

const scope = 'command_history'

type ViewName = 'table' | 'graph' | 'dosing_history'

// The `view` query param picks the active tab; the tab eventKeys are 1/2/3.
const viewToTab = (view: string | null): number => {
  switch (view) {
    case 'graph': return 2
    case 'dosing_history': return 3
    default: return 1
  }
}

const tabToView = (tab: number): ViewName => {
  switch (tab) {
    case 2: return 'graph'
    case 3: return 'dosing_history'
    default: return 'table'
  }
}

interface CommandHistoryProps {
  weightUnit: string
  dosingFilterOptions: DosingFilterOptions
  localization_context?: LocalizationContextType
}

const CommandHistory = ({ weightUnit, dosingFilterOptions, localization_context }: CommandHistoryProps) => {

  const [activeTabKey, setActiveTabKey] = React.useState<number>(() => viewToTab(getParam('view')))

  const handleTabChange = (key: string | null) => {
    if (key === null) return

    const tabKey = Number(key)
    setActiveTabKey(tabKey)

    const params: Record<string, string | null> = { view: tabToView(tabKey) }
    FILTER_PARAM_KEYS.forEach(paramKey => { params[paramKey] = null })
    applyParams(params, 'push')
  }

  React.useEffect(() => {
    const syncTabFromUrl = () => setActiveTabKey(viewToTab(getParam('view')))
    window.addEventListener('popstate', syncTabFromUrl)
    return () => window.removeEventListener('popstate', syncTabFromUrl)
  }, [])

  const tabs = [
    {
      title: t('table_view', { scope }),
      component: <CommandTableView />
    },
    {
      title: t('graph_view', { scope }),
      component: <CommandGraphView localization_context={localization_context} />
    },
    {
      title: t('dosing_amount_history', { scope }),
      component: <DosingAmountHistory weightUnit={weightUnit} filterOptions={dosingFilterOptions} />
    }
  ]

  return (
    <LocalizationContext.Provider value={localization_context || DEFAULT_LOCALIZATION_CONTEXT}>
      <Tabs
        activeKey={activeTabKey}
        transition={false}
        mountOnEnter={true}
        unmountOnExit={true}
        onSelect={handleTabChange}
        fill={true}
        justify={true}
      >
        {map(tabs, (tab, index) => (
             <Tab
             key={`t${index + 1}`}
             eventKey={index + 1}
             title={tab.title}
             className="nav-tabs"
           >
             {tab.component}
           </Tab>
        )
        )}
      </Tabs>
    </LocalizationContext.Provider>
  )
}

export default CommandHistory
